import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";

import { CtaBand, FaqList, PageHero, PeopleStrip } from "@/components/page-parts";
import { EnquiryForm } from "@/components/enquiry-form";
import { FIRM, faqSchema } from "@/lib/firm";

const ILA_FEES = {
  standard: "$990",
  smsf: "$2,000",
} as const;

const FAQS = [
  {
    q: "What is a certificate of independent legal advice?",
    a: "It is a certificate signed by a solicitor confirming they explained the loan, guarantee or security documents to you independently of the borrower and the lender, and that you understood them and signed freely. The lender keeps it as evidence you were properly advised.",
  },
  {
    q: "How much does independent legal advice cost?",
    a: `Our fees start at ${ILA_FEES.standard} including GST for a standard guarantee or loan and security package, and ${ILA_FEES.smsf} including GST where a self managed super fund is involved, because the fund deed, bare trust and limited recourse borrowing arrangement also have to be reviewed. We confirm the fee before the appointment.`,
  },
  {
    q: "Can a conveyancer sign a solicitor's certificate?",
    a: "No. Lenders require a certificate from an Australian legal practitioner holding a current practising certificate with professional indemnity insurance covering the advice. A licensed conveyancer cannot provide it, which is why these matters have to come to a law practice.",
  },
  {
    q: "How quickly can you do it?",
    a: `Most appointments are booked within a day or two and the certificate is issued the same day. Send the documents through or call ${FIRM.phone} and we will confirm the fee and a time.`,
  },
  {
    q: "Can the advice be given by video?",
    a: "Yes, for most lenders. We advise guarantors across Queensland and interstate by video and then arrange execution and witnessing so the certificate meets the lender's requirements. Some lenders insist on a face-to-face appointment — we check their panel requirements first.",
  },
  {
    q: "Can my partner and I see the same solicitor?",
    a: "Not where one of you is the borrower and the other is the guarantor — independence from the person benefiting from the loan is the point of the exercise. Where you are both guaranteeing a third party and your interests align, one solicitor can usually advise you both. We assess conflict at the outset.",
  },
  {
    q: "What do you need from me?",
    a: "The letter of offer or loan agreement, the guarantee and indemnity, any mortgage or general security agreement, and photo ID. For an SMSF matter, also the fund trust deed, the bare or holding trust deed and the contract.",
  },
  {
    q: "Do I have to sign?",
    a: "No. The appointment exists so you can make an informed decision, and part of our job is telling you if we think the exposure is unreasonable or the guarantee could be limited, capped or released on better terms. Those changes have to be raised before you sign.",
  },
];

const WHO_NEEDS_ONE = [
  {
    title: "Parent and family guarantors",
    body: "Guaranteeing a child's home loan, usually secured over the family home. We explain what is at risk, whether the guarantee can be limited or released later, and what happens if the borrower separates, loses work or defaults.",
  },
  {
    title: "Company directors and spouses",
    body: "Business loans, overdrafts, equipment and trade finance almost always come with director guarantees, and often a spouse guarantee secured over a jointly owned home. We cover all-monies wording, cross-collateralisation and exit.",
  },
  {
    title: "Self managed super fund borrowings",
    body: "Limited recourse borrowing arrangements involve the fund deed, a bare trust, the borrowing documents and personal guarantees from the members. We review the structure as well as the guarantee before we certify.",
  },
  {
    title: "Commercial and retail lease guarantors",
    body: "Landlords take personal and bank guarantees on nearly every small business lease. We advise the guarantor, and where the premises are a retail shop we can issue the Form 12 legal advice report at the same time.",
  },
  {
    title: "Family and related-party transactions",
    body: "Loan agreements between family members, granny flat arrangements, property transfers and gift deeds — where both sides need to show the advice was independent for the arrangement to hold up.",
  },
  {
    title: "Consent orders and financial agreements",
    body: "Where each party must have received independent legal advice for the agreement to be binding. We give the advice and sign the certificate.",
  },
];

const WHAT_WE_COVER = [
  "Whether the guarantee is limited to a figure or an all-monies guarantee covering future advances",
  "What property secures it, and whether the lender can sell your home without chasing the borrower first",
  "Events of default, cross-default and what the lender can do, in what order",
  "Interest, default interest, enforcement costs and how the debt grows after default",
  "How and when the guarantee can be released or reduced",
  "Whether a cap, a facility-specific guarantee or lenders mortgage insurance would achieve the same result with less exposure",
  "For SMSF matters — the fund deed, the bare trust, single acquirable asset rules and the limited recourse structure",
];

export const Route = createFileRoute("/independent-legal-advice")({
  head: () => ({
    meta: [
      { title: fitTitle("Independent Legal Advice Certificate QLD | Guarantor Advice") },
      {
        name: "description",
        content:
          fitDescription("Independent legal advice certificates and solicitor's certificates for guarantors, directors and SMSF borrowings in Queensland. Fixed fee from $990 incl GST, same-day turnaround."),
      },
      {
        property: "og:title",
        content: fitTitle(`Independent Legal Advice & Solicitor's Certificates | ${FIRM.name}`),
      },
      {
        property: "og:description",
        content:
          fitDescription("Guarantor and independent legal advice certificates for lenders, brokers and SMSF borrowings — fixed fee, in person or by video, same-day certificate."),
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "/independent-legal-advice" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "/independent-legal-advice" }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) }],
  }),
  component: IndependentLegalAdvicePage,
});

function IndependentLegalAdvicePage() {
  return (
    <>
      <PageHero
        eyebrow="Lending & guarantees"
        title="Independent legal advice certificates for guarantors"
        lead="If your bank has asked you to see your own solicitor and come back with a signed certificate, we do them properly and quickly — in person at Golden Beach or Scarborough, or by video anywhere in Queensland."
      />

      <section className="mx-auto max-w-3xl px-5 py-16">
        <h2 className="font-serif text-3xl text-foreground">
          What a solicitor's certificate is — and what it is not
        </h2>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          A certificate of independent legal advice is signed by a solicitor confirming that they
          explained the loan, guarantee and security documents to you, independently of the borrower
          and the lender, and that you understood them and signed freely. Lenders require it because
          courts have set aside guarantees where the guarantor did not understand the transaction —
          the well-known example being <em>Garcia v National Australia Bank</em>, where a wife
          guaranteed her husband's company debts, received no benefit and had the guarantee set
          aside.
        </p>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          So the certificate protects the lender's ability to enforce. That is fine — but it is not
          the reason to take the appointment seriously. The reason is that a guarantee secured over
          your home is usually the largest financial risk you will ever sign, and this meeting is
          the last point at which it can be limited, capped or refused.
        </p>

        <div className="mt-10 rounded-sm border-l-2 border-accent bg-secondary/50 p-6">
          <h3 className="font-serif text-xl text-foreground">Fixed fees</h3>
          <dl className="mt-4 grid gap-4 sm:grid-cols-2">
            <div className="rounded-sm border border-border bg-card p-5">
              <dt className="text-sm font-semibold text-foreground">
                Standard guarantee or loan package
              </dt>
              <dd className="mt-2 font-serif text-3xl text-foreground">
                From {ILA_FEES.standard}
              </dd>
              <dd className="mt-1 text-xs text-muted-foreground">
                incl. GST — guarantee and indemnity, mortgage or general security agreement, loan
                offer, certificate issued
              </dd>
            </div>
            <div className="rounded-sm border border-border bg-card p-5">
              <dt className="text-sm font-semibold text-foreground">
                Self managed super fund borrowing
              </dt>
              <dd className="mt-2 font-serif text-3xl text-foreground">From {ILA_FEES.smsf}</dd>
              <dd className="mt-1 text-xs text-muted-foreground">
                incl. GST — includes review of the fund deed, bare trust and limited recourse
                borrowing arrangement
              </dd>
            </div>
          </dl>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
            The fee is confirmed before the appointment. More complex facilities — multiple
            guarantors, layered security, corporate trustee structures — are quoted once we have
            seen the documents.
          </p>
        </div>

        <h2 className="mt-16 font-serif text-3xl text-foreground">Who we advise</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {WHO_NEEDS_ONE.map((item) => (
            <div key={item.title} className="rounded-sm border border-border bg-card p-5">
              <h3 className="font-serif text-lg text-foreground">{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.body}</p>
            </div>
          ))}
        </div>

        <h2 className="mt-16 font-serif text-3xl text-foreground">
          What we go through before we certify
        </h2>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          The appointment is advice, not a signing ceremony. Before we sign anything we take you
          through:
        </p>
        <ul className="mt-6 grid gap-3 sm:grid-cols-2">
          {WHAT_WE_COVER.map((item) => (
            <li
              key={item}
              className="rounded-sm border border-border bg-card px-4 py-3 text-sm text-muted-foreground"
            >
              {item}
            </li>
          ))}
        </ul>

        <div className="mt-16 rounded-sm border border-border bg-secondary/50 p-7">
          <h2 className="font-serif text-2xl text-foreground">For brokers, lenders and accountants</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Settlement dates fall over when the guarantor advice is left to the last week. Send the
            documents through and we will confirm the fee and an appointment the same day, advise in
            person or by video, and return the executed certificate straight to you. We are a law
            practice with a Legal Practice Director admitted since 2003, so the certificate meets
            lender panel requirements that a conveyancing-only provider cannot satisfy.
          </p>
          <div className="mt-5 flex flex-wrap gap-4 text-sm font-semibold">
            <Link
              to="/guides/$slug"
              params={{ slug: "independent-legal-advice-for-guarantors-queensland" }}
              className="text-primary underline"
            >
              Guide: what a guarantor is actually signing
            </Link>
            <Link to="/retail-shop-leases" className="text-primary underline">
              Retail shop leases &amp; Form 12
            </Link>
            <Link to="/commercial-conveyancing" className="text-primary underline">
              Commercial conveyancing
            </Link>
          </div>
        </div>

        <PeopleStrip slugs={["michael-klein", "erin-wilkinson"]} heading="Who does the work" />

        <h2 className="mt-16 font-serif text-3xl text-foreground">
          Independent legal advice questions we are asked
        </h2>
        <div className="mt-8">
          <FaqList items={FAQS} />
        </div>

        <div className="mt-16">
          <EnquiryForm heading="Request an independent legal advice appointment" />
        </div>
      </section>

      <CtaBand
        title="Settlement this week?"
        body={`Call ${FIRM.phone} with the loan documents in front of you and we will confirm the fee and a same-day appointment.`}
      />
      <div className="h-20" />
    </>
  );
}
